import { motion } from 'framer-motion';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { StaticCard, TrainingReferenceLink } from '../components/BorderCard';
import { ExternalLink } from 'lucide-react';

const frameworks = {
  bears: {
    title: 'BEARS',
    items: [
      { letter: 'B', text: 'Be Friendly' },
      { letter: 'E', text: 'Engage' },
      { letter: 'A', text: 'Assist' },
      { letter: 'R', text: 'Recommend' },
      { letter: 'S', text: 'Say Thank You' },
    ]
  },
  paws: {
    title: 'PAWS',
    items: [
      { letter: 'P', text: 'Polite Greeting' },
      { letter: 'A', text: 'Ask and Listen' },
      { letter: 'W', text: 'Walk with Customer' },
      { letter: 'S', text: 'Sell with Care' },
    ]
  },
  guest: {
    title: 'GUEST',
    items: [
      { letter: 'G', text: 'Greeting' },
      { letter: 'U', text: 'Understand' },
      { letter: 'E', text: 'Engage' },
      { letter: 'S', text: 'Sale' },
      { letter: 'T', text: 'Thank You' },
    ]
  }
};

const greetings = [
  'Good Morning',
  'Good Afternoon',
  'Welcome to The Bear House',
  'Hey! Welcome to The Bear House'
];

const FrameworkCard = ({ framework }) => (
  <StaticCard className="p-8">
    <h3 className="text-xl font-semibold tracking-[0.15em] text-[#1A1A1A] mb-6 text-center">
      {framework.title}
    </h3>
    <div className="space-y-3">
      {framework.items.map((item, index) => (
        <div key={index} className="flex items-center gap-4 py-2 border-b border-[rgba(26,26,26,0.05)] last:border-b-0">
          {item.letter && (
            <span className="w-8 h-8 flex items-center justify-center border border-[#D32F2F] text-[#D32F2F] font-semibold text-sm">
              {item.letter}
            </span>
          )}
          <span className="text-sm text-[#1A1A1A]">{item.text}</span>
        </div>
      ))}
    </div>
    <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 text-center mt-4">
      For reference only
    </div>
  </StaticCard>
);

const CustomerServicePage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A] mb-4">
            Customer Service Training
          </h1>
          <TrainingReferenceLink 
            href="https://www.canva.com/design/DAG36IVIlok/pkn_QvZxYXXux7SySxB1yg/view?utm_content=DAG36IVIlok&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hd241fb47f9"
          />
        </motion.div>

        {/* Objective */}
        <FadeIn delay={0.2}>
          <StaticCard className="max-w-3xl mx-auto mb-16 p-8 text-center">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1A1A]/60 mb-4">
              Objective
            </h3>
            <p className="text-lg font-light leading-relaxed text-[#1A1A1A]">
              To ensure every customer receives a warm, confident, and style-driven experience 
              reflecting The Bear House philosophy: <span className="font-medium">effortless sophistication</span>, 
              <span className="font-medium"> everyday comfort</span>, and <span className="font-medium">genuine human connection</span>.
            </p>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-4">
              For reference only
            </div>
          </StaticCard>
        </FadeIn>

        {/* Frameworks */}
        <FadeIn delay={0.3}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Framework Blocks
          </h3>
        </FadeIn>
        
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <StaggerItem>
            <FrameworkCard framework={frameworks.bears} />
          </StaggerItem>
          <StaggerItem>
            <FrameworkCard framework={frameworks.paws} />
          </StaggerItem>
          <StaggerItem>
            <FrameworkCard framework={frameworks.guest} />
          </StaggerItem>
        </StaggerContainer>

        {/* Greeting Standards */}
        <FadeIn delay={0.5}>
          <div className="max-w-2xl mx-auto mb-16">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
              Greeting Standards
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {greetings.map((greeting, index) => (
                <StaticCard 
                  key={index}
                  className="p-4 text-center"
                >
                  <span className="text-sm text-[#1A1A1A] italic">"{greeting}"</span>
                </StaticCard>
              ))}
            </div>
          </div>
        </FadeIn>

        {/* Principle */}
        <FadeIn delay={0.6}>
          <StaticCard className="max-w-xl mx-auto text-center py-12 px-8">
            <p className="text-xl font-light text-[#1A1A1A] italic mb-2">
              "Listen first. Style later."
            </p>
            <p className="text-sm text-[#1A1A1A]/60">
              Talk like a stylist, not a salesperson.
            </p>
          </StaticCard>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default CustomerServicePage;
